(() => {
  if (window.__TreatmentSurveyLoaded) return;
  window.__TreatmentSurveyLoaded = true;

  class TreatmentSurvey extends HTMLElement {
    constructor() {
      super();
      this.section = this.dataset.section;
      this.jsonUrl = this.dataset.jsonUrl;
      this.productHandle = this.dataset.productHandle;
      this.enableSurveyMetafield = this.parseMetafieldBoolean(this.dataset.enableSurvey);
      this.requirePdfMetafield = this.parseMetafieldBoolean(this.dataset.requirePdf);

      const productDataEl = this.querySelector(`#TreatmentProductData-${this.section}`);
      if (!productDataEl) {
        console.warn("[TreatmentSurvey] ProductData ontbreekt", this.section);
        return;
      }
      this.productData = JSON.parse(productDataEl.textContent);

      this.loading = this.querySelector(".survey-loading");
      this.stepContainer = this.querySelector(".survey-step-container");
      this.informationContainer = this.querySelector(".survey-information");
      this.resultsContainer = this.querySelector(".survey-results");
      this.variantsContainer = this.querySelector(".survey-results__list");
      this.buyButtonsWrap = this.querySelector(".survey-buy-buttons");

      this.surveyForm = this.querySelector("form");
      this.variantInputsContainer = this.querySelector(`#TreatmentSurveyVariantInputs-${this.section}`);
      this.pdfInput = this.querySelector(`#TreatmentSurveyPdf-${this.section}`);
      this.photosInput = this.querySelector(`#TreatmentSurveyPhotos-${this.section}`);
      this.addBtn = this.querySelector(`#TreatmentSurveyAddBtn-${this.section}`);
      this.requiredMsg = this.querySelector(`#TreatmentSurveyRequired-${this.section}`);
      this.productForm = this.querySelector(".survey-product-form");
      this.baseBtnText =
        (this.addBtn && this.addBtn.querySelector(".survey-btn__text")
          ? this.addBtn.querySelector(".survey-btn__text").textContent.trim()
          : "") || "Bestelle deine Behandlung";

      this.config = null;
      this.answers = { gender: null, locations: [], partner: null, partner_gender: null, quantity: null, photos: [] };
      this.selectedItems = [];
      this.currentIndex = 0;
      this.__purchaseAttempted = false;

      this.init();
      this.patchCartNotification();
    }

    connectedCallback() {
      if (this.__initialized) return;
      const resetBtn = this.querySelector(".survey-results__reset-btn");
      if (resetBtn) {
        resetBtn.addEventListener("click", (e) => {
          e.preventDefault();
          this.resetSurvey();
        });
      }
      if (this.addBtn) {
        this.addBtn.addEventListener("click", (e) => {
          e.preventDefault();
          if (!this.canPurchase()) {
            this.__purchaseAttempted = true;
            this.nudge();
            return;
          }
          this.addToCart();
        });
      }
      this.__initialized = true;
    }

    async init() {
      const spinner = this.loading ? this.loading.querySelector(".loading__spinner") : null;
      if (spinner) spinner.classList.remove("hidden");

      try {
        const response = await fetch(this.jsonUrl);
        const data = await response.json();
        this.config = this.resolveConfig(data);

        if (this.config.enabled === false) {
          this.showFallback();
          return;
        }

        this.buildStepOrder();
        this.currentIndex = 0;
        this.renderCurrentStep();

        if (this.stepContainer) this.stepContainer.style.display = "block";
        if (this.informationContainer) this.informationContainer.style.display = "flex";
        if (this.buyButtonsWrap) this.buyButtonsWrap.style.display = "block";
        if (this.loading) this.loading.style.display = "none";
        if (spinner) spinner.classList.add("hidden");

        this.hideDefaultProductForm();
        this.updateBuyButton();
      } catch (error) {
        console.error("[TreatmentSurvey] init mislukt", error);
        this.showFallback();
      }
    }

    parseMetafieldBoolean(value) {
      if (value == null) return null;
      try {
        const parsed = JSON.parse(value);
        return typeof parsed === "boolean" ? parsed : null;
      } catch (e) {
        return null;
      }
    }

    resolveConfig(data) {
      const defaults = (data && data.defaults) || {};
      const treatments = (data && data.treatments) || {};
      const override = treatments[this.productHandle] || {};
      const config = Object.assign(
        {
          ask_gender: true,
          ask_location: true,
          ask_partner: true,
          ask_quantity: false,
          require_pdf: true,
          ask_photos: false,
          partner_discount_pct: 0,
          match_by: "gender_location",
          location_options: ["Urin", "Oral", "Anal"],
          gender_options: ["Mann", "Frau"],
          quantity_min: 1,
          quantity_max: 30,
          quantity_default: 1,
        },
        { enabled: data ? data.enabled : true },
        defaults,
        override
      );

      // Metafields "Enable survey" en "PDF" uit de Shopify editor hebben voorrang op de JSON-config.
      // Onbekend/niet ingesteld (null) laat de JSON-config ongemoeid, voor bestaande behandelingen.
      // Dit betreft alleen de locatie-vraag (en de bijbehorende variant-matching) - geslacht en
      // partner worden hieronder altijd geforceerd, ongeacht metafields of JSON-config.
      if (this.enableSurveyMetafield === false) {
        config.ask_location = false;
        config.match_by = "default";
      } else if (this.enableSurveyMetafield === true) {
        config.ask_location = true;
        config.match_by = "gender_location";
      }

      if (this.requirePdfMetafield !== null) {
        config.require_pdf = this.requirePdfMetafield;
      }

      // Geslacht en partner-vraag worden altijd gevraagd, ongeacht metafields of JSON-config.
      config.ask_gender = true;
      config.ask_partner = true;

      return config;
    }

    buildStepOrder() {
      const order = [];
      const c = this.config;
      if (c.ask_gender) order.push("gender");
      if (c.ask_location && c.match_by === "gender_location") order.push("location");
      if (c.ask_partner) {
        order.push("partner");
      }
      if (c.require_pdf) order.push("pdf");
      if (c.ask_photos) order.push("photos");
      order.push("summary");
      this.stepOrder = order;
    }

    get questionStepCount() {
      return this.stepOrder.filter((s) => s !== "summary").length;
    }

    /* =========================
       Rendering van stappen
       ========================= */
    renderCurrentStep() {
      this.buildStepOrder();
      if (this.currentIndex >= this.stepOrder.length) {
        this.currentIndex = this.stepOrder.length - 1;
      }
      const stepName = this.stepOrder[this.currentIndex];

      if (stepName === "summary") {
        this.renderSummary();
        return;
      }

      // Vragenmodus tonen
      this.showResultsView(false);
      this.stepContainer.innerHTML = "";

      let stepEl;
      switch (stepName) {
        case "gender":
          stepEl = this.buildChoiceStep({
            title: "Was ist dein Geschlecht?",
            description: "Damit bestimmen wir die passende Behandlung für dich.",
            name: "gender",
            type: "radio",
            options: this.config.gender_options,
            value: this.answers.gender,
          });
          break;
        case "location": {
          const gender = this.answers.gender;
          let locOptions = [...this.config.location_options];
          if (gender === "Frau") {
            locOptions = locOptions.map((o) => (o === "Urin" ? "Vaginal" : o));
          }
          stepEl = this.buildChoiceStep({
            title: "Worauf wurdest du positiv getestet?",
            description: "Mehrfachauswahl möglich. Wähle aus, worauf das positive Ergebnis lautete.",
            name: "locations",
            type: "checkbox",
            options: locOptions,
            value: this.answers.locations,
          });
          break;
        }
        case "partner":
          stepEl = this.buildPartnerStep();
          break;
        case "pdf":
          stepEl = this.buildPdfStep();
          break;
        case "photos":
          stepEl = this.buildPhotosStep();
          break;
      }

      if (stepEl) {
        stepEl.classList.add("active");
        this.stepContainer.appendChild(stepEl);
      }
      this.updateBuyButton();
    }

    plainHeader(title, description) {
      const wrap = document.createElement("div");
      const label = document.createElement("label");
      label.className = "survey-step__title";
      label.textContent = title;
      wrap.appendChild(label);
      if (description) {
        const desc = document.createElement("p");
        desc.className = "survey-step__description";
        desc.textContent = description;
        wrap.appendChild(desc);
      }
      return wrap;
    }

    stepHeader(title, description) {
      const total = this.questionStepCount;
      const current = this.currentIndex + 1;
      const wrap = document.createElement("div");

      const label = document.createElement("label");
      label.className = "survey-step__title";
      label.textContent = `${current}/${total} - ${title}`;
      wrap.appendChild(label);

      if (description) {
        const desc = document.createElement("p");
        desc.className = "survey-step__description";
        desc.textContent = description;
        wrap.appendChild(desc);
      }
      return wrap;
    }

    buildChoiceStep({ title, description, name, type, options, value }) {
      const step = document.createElement("div");
      step.className = "survey-step";
      step.appendChild(this.stepHeader(title, description));

      const optionsDiv = document.createElement("div");
      optionsDiv.className =
        type === "radio" ? "survey-options survey-options--radio" : "survey-options survey-options--checkbox";

      options.forEach((option, idx) => {
        const optionDiv = document.createElement("div");
        optionDiv.className = "survey-option";

        const input = document.createElement("input");
        input.type = type;
        input.id = `ts-${name}-${idx}-${this.section}`;
        input.name = type === "radio" ? `treatment[${name}]` : `treatment[${name}][]`;
        input.value = option;
        input.className = "survey-option__input";

        if (type === "checkbox" && Array.isArray(value)) {
          input.checked = value.includes(option);
        } else if (type === "radio") {
          input.checked = value === option;
        }

        const label = document.createElement("label");
        label.htmlFor = input.id;
        label.className = "survey-option__label";
        label.textContent = option;

        optionDiv.appendChild(input);
        optionDiv.appendChild(label);
        optionsDiv.appendChild(optionDiv);

        input.addEventListener("change", () => {
          if (type === "radio") {
            this.answers[name] = input.value;
            this.buildStepOrder();
            // Auto-doorklikken, net als de product survey
            window.clearTimeout(this.__advanceTimer);
            this.__advanceTimer = window.setTimeout(() => {
              this.currentIndex += 1;
              this.renderCurrentStep();
            }, 250);
          } else {
            const checked = Array.from(optionsDiv.querySelectorAll("input:checked")).map((el) => el.value);
            this.answers[name] = checked;
            this.updateNavState(step, name);
          }
        });
      });

      step.appendChild(optionsDiv);
      if (type !== "radio") step.appendChild(this.buildNav(name));
      return step;
    }

    buildPartnerStep() {
      const step = document.createElement("div");
      step.className = "survey-step";
      step.appendChild(
        this.stepHeader(
          "Möchtest du direkt auch für deine Sexualpartnerin oder deinen Sexualpartner bestellen?",
          "Mit einem Testergebnis kannst du eine Behandlung für dich und deine Partnerin oder deinen Partner bestellen."
        )
      );

      // --- Ja/Nee opties ---
      const partnerOptions = document.createElement("div");
      partnerOptions.className = "survey-options survey-options--radio";

      ["Ja", "Nee"].forEach((option, idx) => {
        const div = document.createElement("div");
        div.className = "survey-option";

        const input = document.createElement("input");
        input.type = "radio";
        input.id = `ts-partner-${idx}-${this.section}`;
        input.name = `treatment[partner]`;
        input.value = option;
        input.className = "survey-option__input";
        input.checked = this.answers.partner === option;

        const label = document.createElement("label");
        label.htmlFor = input.id;
        label.className = "survey-option__label";
        label.textContent = option;

        div.appendChild(input);
        div.appendChild(label);
        partnerOptions.appendChild(div);
      });

      step.appendChild(partnerOptions);

      // --- Sub-vraag: geslacht partner (verborgen totdat Ja gekozen) ---
      const subStep = document.createElement("div");
      subStep.className = "survey-step active treatment-survey__sub-step";
      if (this.answers.partner !== "Ja") subStep.classList.add("treatment-survey__sub-step--hidden");
      subStep.appendChild(
        this.plainHeader("Was ist das Geschlecht deiner Partnerin oder deines Partners?", "So stellen wir die passende Behandlung zusammen.")
      );

      const genderOptions = document.createElement("div");
      genderOptions.className = "survey-options survey-options--radio";

      this.config.gender_options.forEach((option, idx) => {
        const div = document.createElement("div");
        div.className = "survey-option";

        const input = document.createElement("input");
        input.type = "radio";
        input.id = `ts-partner-gender-${idx}-${this.section}`;
        input.name = `treatment[partner_gender]`;
        input.value = option;
        input.className = "survey-option__input";
        input.checked = this.answers.partner_gender === option;

        const label = document.createElement("label");
        label.htmlFor = input.id;
        label.className = "survey-option__label";
        label.textContent = option;

        div.appendChild(input);
        div.appendChild(label);
        genderOptions.appendChild(div);

        input.addEventListener("change", () => {
          this.answers.partner_gender = input.value;
          // Beide antwoorden compleet → doorklikken
          window.clearTimeout(this.__advanceTimer);
          this.__advanceTimer = window.setTimeout(() => {
            this.currentIndex += 1;
            this.renderCurrentStep();
          }, 250);
        });
      });

      subStep.appendChild(genderOptions);
      step.appendChild(subStep);

      // Ja/Nee change handler
      partnerOptions.querySelectorAll("input").forEach((input) => {
        input.addEventListener("change", () => {
          this.answers.partner = input.value;
          if (input.value === "Nee") {
            this.answers.partner_gender = null;
            subStep.classList.add("treatment-survey__sub-step--hidden");
            // Nee gekozen → meteen doorklikken
            window.clearTimeout(this.__advanceTimer);
            this.__advanceTimer = window.setTimeout(() => {
              this.currentIndex += 1;
              this.renderCurrentStep();
            }, 250);
          } else {
            subStep.classList.remove("treatment-survey__sub-step--hidden");
          }
        });
      });

      return step;
    }

    buildPdfStep() {
      const step = document.createElement("div");
      step.className = "survey-step";
      step.appendChild(
        this.stepHeader(
          "Lade dein Testergebnis hoch (PDF)",
          "Unsere Ärztin oder unser Arzt benötigt dein positives Testergebnis, um eine Behandlung ausstellen zu können."
        )
      );

      const dropzone = document.createElement("div");
      dropzone.className = "treatment-survey__dropzone";

      dropzone.innerHTML = `
        <svg class="treatment-survey__dropzone-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <span class="treatment-survey__dropzone-text">Ziehe deine PDF hierher oder <span class="treatment-survey__dropzone-link">kies een bestand</span></span>
        <span class="treatment-survey__dropzone-hint">Nur PDF • max. 20 MB</span>
      `;

      const fileName = document.createElement("span");
      fileName.className = "treatment-survey__pdf-name";
      fileName.textContent = this.answers.pdfName || "";

      const status = document.createElement("p");
      status.className = "treatment-survey__pdf-status";

      const progressWrap = document.createElement("div");
      progressWrap.className = "treatment-survey__pdf-progress treatment-survey__pdf-progress--hidden";

      const progressLabel = document.createElement("span");
      progressLabel.className = "treatment-survey__pdf-progress-label";
      progressLabel.textContent = "Ergebnis wird geladen ...";

      const progressBar = document.createElement("div");
      progressBar.className = "treatment-survey__pdf-progress-bar";

      const progressFill = document.createElement("div");
      progressFill.className = "treatment-survey__pdf-progress-fill";

      progressBar.appendChild(progressFill);
      progressWrap.appendChild(progressLabel);
      progressWrap.appendChild(progressBar);

      const handleFile = (file) => {
        if (!file) return;
        const max = 20 * 1024 * 1024;
        const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
        if (!isPdf || file.size > max) {
          this.pdfInput.value = "";
          this.answers.pdfName = "";
          fileName.textContent = "";
          status.textContent = "Lade eine PDF-Datei hoch (max. 20 MB).";
          status.classList.add("error");
          dropzone.classList.remove("treatment-survey__dropzone--active");
          return;
        }
        status.textContent = "";
        status.classList.remove("error");
        this.answers.pdfName = file.name;
        fileName.textContent = file.name;
        dropzone.classList.add("treatment-survey__dropzone--done");

        progressWrap.classList.remove("treatment-survey__pdf-progress--hidden");
        progressFill.style.width = "0%";
        let pct = 0;
        const interval = setInterval(() => {
          pct += 2;
          progressFill.style.width = pct + "%";
          if (pct >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              this.currentIndex += 1;
              this.renderCurrentStep();
            }, 200);
          }
        }, 30);
      };

      dropzone.addEventListener("click", () => this.pdfInput.click());
      dropzone.addEventListener("dragover", (e) => {
        e.preventDefault();
        dropzone.classList.add("treatment-survey__dropzone--active");
      });
      dropzone.addEventListener("dragleave", () => dropzone.classList.remove("treatment-survey__dropzone--active"));
      dropzone.addEventListener("drop", (e) => {
        e.preventDefault();
        dropzone.classList.remove("treatment-survey__dropzone--active");
        const file = e.dataTransfer && e.dataTransfer.files[0];
        handleFile(file);
      });

      this.pdfInput.onchange = () => handleFile(this.pdfInput.files && this.pdfInput.files[0]);

      step.appendChild(dropzone);
      step.appendChild(fileName);
      step.appendChild(status);
      step.appendChild(progressWrap);
      return step;
    }

    buildPhotosStep() {
      const step = document.createElement("div");
      step.className = "survey-step";
      step.appendChild(
        this.stepHeader(
          "Fotos der betroffenen Stelle",
          "Für eine gute medizinische Beurteilung bitten wir dich, deutliche Fotos der betroffenen Stelle(n) hochzuladen (mindestens 1). Diese werden ausschließlich von der behandelnden Ärztin oder dem behandelnden Arzt zur Beurteilung möglicher Feigwarzen verwendet."
        )
      );

      const MAX_PHOTOS = 5;
      const MAX_SIZE = 20 * 1024 * 1024;

      const dropzone = document.createElement("div");
      dropzone.className = "treatment-survey__dropzone";
      dropzone.innerHTML = `
        <svg class="treatment-survey__dropzone-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <span class="treatment-survey__dropzone-text">Ziehe deine Fotos hierher oder <span class="treatment-survey__dropzone-link">kies bestanden</span></span>
        <span class="treatment-survey__dropzone-hint">JPG oder PNG • mindestens 1, max. ${MAX_PHOTOS} Fotos • max. 20 MB pro Foto</span>
      `;

      const list = document.createElement("ul");
      list.className = "treatment-survey__photo-list";

      const status = document.createElement("p");
      status.className = "treatment-survey__pdf-status";

      const renderList = () => {
        list.innerHTML = "";
        this.answers.photos.forEach((file, idx) => {
          const li = document.createElement("li");
          li.className = "treatment-survey__photo-item";

          const nameSpan = document.createElement("span");
          nameSpan.textContent = file.name;
          li.appendChild(nameSpan);

          const removeBtn = document.createElement("button");
          removeBtn.type = "button";
          removeBtn.className = "treatment-survey__photo-remove";
          removeBtn.setAttribute("aria-label", `Verwijder ${file.name}`);
          removeBtn.textContent = "×";
          removeBtn.addEventListener("click", () => {
            this.answers.photos.splice(idx, 1);
            renderList();
            this.updateNavState(step, "photos");
          });
          li.appendChild(removeBtn);

          list.appendChild(li);
        });
      };

      const handleFiles = (fileList) => {
        const files = Array.from(fileList || []);
        status.textContent = "";
        status.classList.remove("error");

        for (const file of files) {
          if (this.answers.photos.length >= MAX_PHOTOS) {
            status.textContent = `Je kan maximaal ${MAX_PHOTOS} foto's uploaden.`;
            status.classList.add("error");
            break;
          }
          const isImage = /^image\//.test(file.type) || /\.(jpe?g|png|heic|webp)$/i.test(file.name);
          if (!isImage || file.size > MAX_SIZE) {
            status.textContent = "Lade nur Fotos (JPG/PNG) mit max. 20 MB hoch.";
            status.classList.add("error");
            continue;
          }
          this.answers.photos.push(file);
        }
        renderList();
        this.updateNavState(step, "photos");
      };

      dropzone.addEventListener("click", () => this.photosInput.click());
      dropzone.addEventListener("dragover", (e) => {
        e.preventDefault();
        dropzone.classList.add("treatment-survey__dropzone--active");
      });
      dropzone.addEventListener("dragleave", () => dropzone.classList.remove("treatment-survey__dropzone--active"));
      dropzone.addEventListener("drop", (e) => {
        e.preventDefault();
        dropzone.classList.remove("treatment-survey__dropzone--active");
        handleFiles(e.dataTransfer && e.dataTransfer.files);
      });

      this.photosInput.onchange = () => {
        handleFiles(this.photosInput.files);
        this.photosInput.value = "";
      };

      renderList();

      step.appendChild(dropzone);
      step.appendChild(list);
      step.appendChild(status);
      step.appendChild(this.buildNav("photos"));
      return step;
    }

    buildNav(name) {
      const nav = document.createElement("div");
      nav.className = "treatment-survey__nav";

      const next = document.createElement("button");
      next.type = "button";
      next.className = "button survey-step__next-btn treatment-survey__next-btn";
      next.textContent = this.stepOrder[this.currentIndex + 1] === "summary" ? "Zur Übersicht" : "Weiter";
      next.disabled = !this.isStepValid(name);
      next.addEventListener("click", () => {
        if (!this.isStepValid(name)) return;
        this.currentIndex += 1;
        this.renderCurrentStep();
      });
      nav.appendChild(next);

      return nav;
    }

    updateNavState(stepEl, name) {
      const next = stepEl.querySelector(".treatment-survey__next-btn");
      if (next) {
        next.disabled = !this.isStepValid(name);
        // Stap-volgorde kan veranderen (partner Ja/Nee)
        this.buildStepOrder();
        next.textContent = this.stepOrder[this.currentIndex + 1] === "summary" ? "Zur Übersicht" : "Weiter";
      }
    }

    isStepValid(name) {
      switch (name) {
        case "gender":
          return !!this.answers.gender;
        case "locations":
          return Array.isArray(this.answers.locations) && this.answers.locations.length > 0;
        case "partner":
          return !!this.answers.partner;
        case "partner_gender":
          return !!this.answers.partner_gender;
        case "pdf":
          return this.config.require_pdf ? !!this.answers.pdfName : true;
        case "photos":
          return Array.isArray(this.answers.photos) && this.answers.photos.length > 0;
        case "quantity":
          return (parseInt(this.answers.quantity, 10) || 0) >= (parseInt(this.config.quantity_min, 10) || 1);
        default:
          return true;
      }
    }

    buildQuantityStep() {
      const step = document.createElement("div");
      step.className = "survey-step";
      step.appendChild(this.stepHeader("Wie viel möchtest du bestellen?", "Wähle die gewünschte Menge."));

      const min = parseInt(this.config.quantity_min, 10) || 1;
      const max = parseInt(this.config.quantity_max, 10) || 0;
      if (this.answers.quantity == null) {
        this.answers.quantity = parseInt(this.config.quantity_default, 10) || min;
      }
      let value = Math.max(min, parseInt(this.answers.quantity, 10) || min);
      if (max) value = Math.min(max, value);
      this.answers.quantity = value;

      const minusSvg =
        '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path></svg>';
      const plusSvg =
        '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path></svg>';

      // Zelfde markup/styling als de standaard product-page quantity selector
      const wrap = document.createElement("div");
      wrap.className = "product-form__input product-form__quantity treatment-survey__qty";
      wrap.innerHTML = `
        <quantity-input class="quantity">
          <button class="quantity__button" name="minus" type="button">
            <span class="visually-hidden">Menge verringern</span>
            ${minusSvg}
          </button>
          <input
            class="quantity__input"
            type="number"
            name="ts-quantity"
            inputmode="numeric"
            min="${min}"
            ${max ? `max="${max}"` : ""}
            step="1"
            value="${value}"
            data-min="${min}"
          >
          <button class="quantity__button" name="plus" type="button">
            <span class="visually-hidden">Menge erhöhen</span>
            ${plusSvg}
          </button>
        </quantity-input>`;

      const input = wrap.querySelector(".quantity__input");
      wrap.addEventListener("change", () => {
        let v = parseInt(input.value, 10) || min;
        v = Math.max(min, v);
        if (max) v = Math.min(max, v);
        this.answers.quantity = v;
        this.updateNavState(step, "quantity");
      });

      step.appendChild(wrap);
      step.appendChild(this.buildNav("quantity"));
      return step;
    }

    /* =========================
       Variant-matching
       ========================= */
    canonGender(value) {
      if (!value) return "";
      const v = String(value).toLowerCase();
      if (v.includes("vrouw") || v.includes("frau") || v.includes("female") || v.includes("dame")) return "vrouw";
      if (v.includes("mann") || v.includes("man") || v.includes("male") || v.includes("herr") || v.includes("heer")) return "man";
      if (v.includes("anders") || v.includes("divers") || v.includes("other") || v.includes("non-binary")) return "anders";
      return "";
    }

    canonLocation(value) {
      if (!value) return "";
      const v = String(value).toLowerCase();
      if (v.includes("urin")) return "urine";
      if (v.includes("vagina")) return "vaginaal";
      if (v.includes("oraal") || v.includes("oral") || v.includes("keel")) return "oraal";
      if (v.includes("anaal") || v.includes("anal")) return "anaal";
      return "";
    }

    // Vertaalt de gekozen uitslag-locatie naar de juiste variant-locatie per geslacht.
    // Man/Anders -> Urine, Vrouw -> Vaginaal; Oraal en Anaal zijn voor beide gelijk.
    effectiveLocation(gender, location) {
      const cg = this.canonGender(gender);
      const cl = this.canonLocation(location);
      if (cl === "urine" || cl === "vaginaal") {
        return cg === "vrouw" ? "Vaginal" : "Urin";
      }
      return location;
    }

    firstAvailableVariant() {
      const variants = this.productData.variants || [];
      return variants.find((v) => v.available !== false) || variants[0] || null;
    }

    findVariant(gender, location) {
      const cg = this.canonGender(gender);
      const cl = location ? this.canonLocation(location) : null;
      const variants = (this.productData.variants || []).filter((v) => v.available !== false);

      let best = null;
      let bestScore = -1;

      for (const v of variants) {
        const hay = `${v.option1 || ""} ${v.option2 || ""} ${v.option3 || ""} ${v.title || ""}`.toLowerCase();
        const variantGender = this.canonGender(hay);

        let locMatch = true;
        if (cl) {
          // Canoniek tegen canoniek vergelijken: de varianttitel is Duits
          // ("Vaginal"), het token Nederlands ("vaginaal") - includes() faalt daarop.
          locMatch = this.canonLocation(hay) === cl;
          // Bij vrouwen kan een urine-locatie als vaginaal in de variant staan
          if (!locMatch && cl === "urine" && cg === "vrouw" && hay.includes("vaginaal")) locMatch = true;
        }
        if (cl && !locMatch) continue;

        let score = 0;
        if (cl && locMatch) score += 2;
        if (cg && variantGender === cg) score += 2;
        else if (cg && variantGender && variantGender !== cg) score -= 1;

        if (score > bestScore) {
          bestScore = score;
          best = v;
        }
      }
      return best;
    }

    buildSelection() {
      const items = [];
      const matchBy = this.config.match_by;
      const gender = this.answers.gender;
      const locations = Array.isArray(this.answers.locations) ? this.answers.locations : [];

      const collectFor = (forGender, owner, discountPct) => {
        if (matchBy === "gender_location" && locations.length) {
          // Eén regel per persoon, ongeacht hoeveel locaties positief zijn getest.
          // De variant wordt bepaald op basis van de eerst gekozen locatie; alle gekozen
          // locaties worden samengevoegd in de "Bereich"-property op die ene regel.
          const effectiveLocs = [...new Set(locations.map((loc) => this.effectiveLocation(forGender, loc)))];
          const variant =
            this.findVariant(forGender, effectiveLocs[0]) ||
            this.findVariant(forGender, null) ||
            this.firstAvailableVariant();
          if (variant) {
            items.push({ variant, label: effectiveLocs.join(", "), gender: forGender, owner, discountPct });
          }
        } else if (matchBy === "gender") {
          const variant = this.findVariant(forGender, null) || this.firstAvailableVariant();
          if (variant) items.push({ variant, label: null, gender: forGender, owner, discountPct });
        } else {
          const variant = this.firstAvailableVariant();
          if (variant) items.push({ variant, label: null, gender: forGender, owner, discountPct });
        }
      };

      collectFor(gender, "self", 0);

      if (this.answers.partner === "Ja") {
        collectFor(this.answers.partner_gender || gender, "partner", this.config.partner_discount_pct || 0);
      }

      const qty = this.config.ask_quantity
        ? Math.max(1, parseInt(this.answers.quantity, 10) || parseInt(this.config.quantity_default, 10) || 1)
        : 1;
      items.forEach((it) => {
        if (it.quantity == null) it.quantity = it.owner === "self" ? qty : 1;
      });

      this.selectedItems = items;
      return items;
    }

    /* =========================
       Samenvatting / Resultaten
       ========================= */
    renderSummary() {
      this.buildSelection();

      if (this.stepContainer) this.stepContainer.style.display = "none";
      if (this.informationContainer) this.informationContainer.style.display = "none";

      this.variantsContainer.innerHTML = "";

      const resultsTitle = this.resultsContainer.querySelector(".survey-results__title");
      const noResults = this.resultsContainer.querySelector(".survey-results__no-results");

      // Bestaande totals/back verwijderen om te herrenderen
      this.resultsContainer.querySelectorAll(".survey-results__total, .treatment-survey__summary-back").forEach((el) => el.remove());

      if (!this.selectedItems.length) {
        resultsTitle.style.display = "none";
        noResults.style.display = "block";
        this.showResultsView(true);
        this.updateBuyButton();
        return;
      }

      resultsTitle.style.display = "block";
      noResults.style.display = "none";

      this.selectedItems.forEach((item, index) => {
        this.variantsContainer.appendChild(this.createItemListEl(item, index));
      });
      this.addFreePerksToResults();

      this.appendTotals();
      this.updateVariantInputs();
      this.showResultsView(true);
      this.updateBuyButton();
    }

    renderSummaryFromSelection() {
      this.variantsContainer.innerHTML = "";
      this.resultsContainer
        .querySelectorAll(".survey-results__total, .treatment-survey__summary-back")
        .forEach((el) => el.remove());
      this.selectedItems.forEach((item, index) => {
        this.variantsContainer.appendChild(this.createItemListEl(item, index));
      });
      this.addFreePerksToResults();
      this.appendTotals();
      this.updateVariantInputs();
      this.updateBuyButton();
    }

    createItemListEl(item, index) {
      const li = document.createElement("li");
      li.className = "survey-variant";
      li.dataset.itemIndex = index;

      const ownerLabel = item.owner === "partner" ? "Partner" : "Für mich selbst";
      const locText = item.label ? ` – ${item.label}` : "";
      const title = `${item.variant.product_title || this.productData.product.title}${locText}`;

      const qty = item.quantity || 1;
      const priceCents = (item.variant.price || 0) * qty;
      const discounted = Math.round(priceCents * (1 - (item.discountPct || 0)));

      let priceHtml = `<span class="survey-variant__price-new">${this.formatMoney(discounted)}</span>`;
      if ((item.discountPct || 0) > 0) {
        priceHtml = `
          <span class="survey-variant__price-old">${this.formatMoney(priceCents)}</span>
          <span class="survey-variant__price-new">${this.formatMoney(discounted)}</span>`;
      }

      li.innerHTML = `
        <div class="survey-variant__title">
          <div class="survey-variant__title-text">
            <div class="survey-variant__title-main">${qty} x ${title}</div>
            <span class="treatment-survey__owner-tag">Für: ${ownerLabel}</span>
          </div>
        </div>
        <div class="survey-variant__price">${priceHtml}</div>
        <button class="survey-variant__remove-btn" type="button" title="Aus Auswahl entfernen">
          <span class="visually-hidden">${title} aus Auswahl entfernen</span>
        </button>
      `;

      li.querySelector(".survey-variant__remove-btn").addEventListener("click", (e) => {
        e.stopPropagation();
        this.removeItem(index);
      });

      if (this.config.ask_quantity && item.owner === "self") {
        const min = parseInt(this.config.quantity_min, 10) || 1;
        const max = parseInt(this.config.quantity_max, 10) || 0;
        const minusSvg = '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path></svg>';
        const plusSvg = '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path></svg>';
        const qtyWrap = document.createElement("div");
        qtyWrap.className = "product-form__input product-form__quantity treatment-survey__qty";
        qtyWrap.innerHTML = `
          <quantity-input class="quantity">
            <button class="quantity__button" name="minus" type="button">
              <span class="visually-hidden">Menge verringern</span>
              ${minusSvg}
            </button>
            <input
              class="quantity__input"
              type="number"
              inputmode="numeric"
              min="${min}"
              ${max ? `max="${max}"` : ""}
              value="${qty}"
              data-min="${min}"
            >
            <button class="quantity__button" name="plus" type="button">
              <span class="visually-hidden">Menge erhöhen</span>
              ${plusSvg}
            </button>
          </quantity-input>`;
        const qtyInput = qtyWrap.querySelector(".quantity__input");
        qtyWrap.addEventListener("change", () => {
          let v = parseInt(qtyInput.value, 10) || min;
          v = Math.max(min, v);
          if (max) v = Math.min(max, v);
          item.quantity = v;
          this.answers.quantity = v;
          this.renderSummaryFromSelection();
        });
        li.appendChild(qtyWrap);
      }

      return li;
    }

    removeItem(index) {
      this.selectedItems.splice(index, 1);
      if (!this.selectedItems.length) {
        this.resetSurvey();
      } else {
        this.renderSummaryFromSelection();
      }
    }

    getPerks() {
      return [
        { key: "medical_consult", title: "Medizinische Beratung", valueCents: 2500 },
      ];
    }

    addFreePerksToResults() {
      // Free perks are now shown in totals section, not in results list
      return;
    }

    appendTotals() {
      const variantsSubtotal = this.selectedItems.reduce((sum, i) => sum + (i.variant.price || 0) * (i.quantity || 1), 0);
      const variantsTotal = this.selectedItems.reduce(
        (sum, i) => sum + Math.round((i.variant.price || 0) * (i.quantity || 1) * (1 - (i.discountPct || 0))),
        0
      );
      
      const perks = this.getPerks();
      const medicalValue = perks.find(p => p.key === 'medical_consult')?.valueCents || 2500;
      
      const subtotal = variantsSubtotal; // Only products, not perks
      const total = variantsTotal;
      const discount = variantsSubtotal - variantsTotal; // Only product discount

      const wrap = document.createElement("div");
      wrap.className = "survey-results__total";
      wrap.innerHTML = `
        <div class="survey-results__totals">
          <div class="survey-results__totals-row">
            <span class="survey-results__totals-label">Zwischensumme:</span>
            <span class="survey-results__totals-value">${this.formatMoney(subtotal)}</span>
          </div>
          
          <div class="survey-results__totals-row survey-results__totals-row--perks">
            <span class="survey-results__totals-label">Medizinische Begleitung:</span>
            <span class="survey-results__totals-value survey-results__totals-value--gratis">GRATIS</span>
          </div>
          ${
            discount > 0
              ? `<div class="survey-results__totals-row survey-results__totals-row--discount">
                   <span class="survey-results__totals-label">Rabatt:</span>
                   <span class="survey-results__totals-value">−${this.formatMoney(discount)}</span>
                 </div>`
              : ""
          }
          <div class="survey-results__totals-row survey-results__totals-row--total">
            <span class="survey-results__totals-label">Aktueller Preis:</span>
            <span class="survey-results__totals-value">${this.formatMoney(total)}</span>
          </div>
        </div>`;
      this.variantsContainer.insertAdjacentElement("afterend", wrap);
    }

    showResultsView(show) {
      // Koopknoppen blijven altijd zichtbaar, net als bij de product survey.
      if (this.resultsContainer) this.resultsContainer.style.display = show ? "block" : "none";
      if (this.stepContainer) this.stepContainer.style.display = show ? "none" : "block";
    }

    /* =========================
       Cart
       ========================= */
    updateVariantInputs() {
      if (!this.variantInputsContainer) return;
      this.variantInputsContainer.innerHTML = "";

      const dummy = this.surveyForm.querySelector('[name="id"]');
      if (dummy) dummy.disabled = true;

      this.selectedItems.forEach((item, idx) => {
        const id = document.createElement("input");
        id.type = "hidden";
        id.name = `items[${idx}][id]`;
        id.value = item.variant.id;
        this.variantInputsContainer.appendChild(id);

        const qty = document.createElement("input");
        qty.type = "hidden";
        qty.name = `items[${idx}][quantity]`;
        qty.value = String(item.quantity || 1);
        this.variantInputsContainer.appendChild(qty);

        const owner = document.createElement("input");
        owner.type = "hidden";
        owner.name = `items[${idx}][properties][Behandlung für]`;
        owner.value = item.owner === "partner" ? "Partner" : "Für mich selbst";
        this.variantInputsContainer.appendChild(owner);

        if (item.label) {
          const locInput = document.createElement("input");
          locInput.type = "hidden";
          locInput.name = `items[${idx}][properties][Bereich]`;
          locInput.value = item.label;
          this.variantInputsContainer.appendChild(locInput);
        }

        if (item.owner === "partner") {
          const flag = document.createElement("input");
          flag.type = "hidden";
          flag.name = `items[${idx}][properties][_partner]`;
          flag.value = "true";
          this.variantInputsContainer.appendChild(flag);
        }
      });

      // PDF koppelen aan het eerste item (indien geüpload)
      const hasPdf = this.pdfInput && this.pdfInput.files && this.pdfInput.files[0];
      if (hasPdf && this.selectedItems.length) {
        this.pdfInput.name = "items[0][properties][Testergebnis]";
      } else {
        this.pdfInput.removeAttribute("name");
      }

      // Optionele foto's koppelen aan het eerste item; elk bestand krijgt een eigen property
      const photos = Array.isArray(this.answers.photos) ? this.answers.photos : [];
      if (photos.length && this.selectedItems.length) {
        photos.forEach((file, idx) => {
          const dt = new DataTransfer();
          dt.items.add(file);
          const input = document.createElement("input");
          input.type = "file";
          input.style.display = "none";
          input.name = `items[0][properties][Foto ${idx + 1}]`;
          input.files = dt.files;
          this.variantInputsContainer.appendChild(input);
        });
      }

      if ((hasPdf || photos.length) && this.selectedItems.length) {
        this.surveyForm.setAttribute("enctype", "multipart/form-data");
      }
    }

    canPurchaseSilent() {
      if (!this.selectedItems.length) return false;
      if (this.config.require_pdf && !(this.pdfInput && this.pdfInput.files && this.pdfInput.files[0])) return false;
      if (this.config.ask_photos && !(Array.isArray(this.answers.photos) && this.answers.photos.length > 0)) return false;
      return this.selectedItems.filter((i) => i.variant.available !== false).length > 0;
    }

    canPurchase() {
      if (!this.selectedItems.length) return false;
      if (this.config.require_pdf && !(this.pdfInput && this.pdfInput.files && this.pdfInput.files[0])) {
        this.handleError("Lade zuerst dein Testergebnis (PDF) hoch, um fortzufahren.");
        return false;
      }
      if (this.config.ask_photos && !(Array.isArray(this.answers.photos) && this.answers.photos.length > 0)) {
        this.handleError("Lade zuerst mindestens 1 Foto hoch, um fortzufahren.");
        return false;
      }
      const available = this.selectedItems.filter((i) => i.variant.available !== false);
      return available.length > 0;
    }

    updateBuyButton() {
      const onSummary = this.stepOrder && this.stepOrder[this.currentIndex] === "summary";
      const count = this.selectedItems.filter((i) => i.variant.available !== false).length;
      const textEl = this.addBtn ? this.addBtn.querySelector(".survey-btn__text") : null;
      if (textEl) {
        textEl.textContent = onSummary && count > 0 ? `${this.baseBtnText} (${count})` : this.baseBtnText;
      }
      if (this.requiredMsg) {
        this.requiredMsg.hidden = !(this.__purchaseAttempted && !this.canPurchaseSilent());
      }
    }

    nudge() {
      if (this.requiredMsg) this.requiredMsg.hidden = false;
      const target = this.querySelector(".survey-configurator__container") || this;
      if (target) {
        target.classList.remove("survey-shake");
        void target.offsetWidth;
        target.classList.add("survey-shake");
        window.clearTimeout(this.__shakeTimeout);
        this.__shakeTimeout = window.setTimeout(() => target.classList.remove("survey-shake"), 600);
      }
    }

    handleError(message) {
      const wrap = this.querySelector(".product-form__error-message-wrapper");
      const msg = this.querySelector(".product-form__error-message");
      if (wrap && msg) {
        msg.textContent = message || "";
        wrap.hidden = !message;
      }
    }

    async addToCart() {
      if (!this.selectedItems.length) return;
      if (this.addBtn?.getAttribute("aria-disabled") === "true") return;

      this.updateVariantInputs();

      const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      if (cart) {
        this.surveyForm.querySelectorAll('[name="sections"], [name="sections_url"]').forEach((i) => i.remove());
        const sections = cart.getSectionsToRender().map((s) => s.id);

        const sectionsInput = document.createElement("input");
        sectionsInput.type = "hidden";
        sectionsInput.name = "sections";
        sectionsInput.value = sections.join(",");
        this.surveyForm.appendChild(sectionsInput);

        const sectionsUrlInput = document.createElement("input");
        sectionsUrlInput.type = "hidden";
        sectionsUrlInput.name = "sections_url";
        sectionsUrlInput.value = window.location.pathname;
        this.surveyForm.appendChild(sectionsUrlInput);

        cart.setActiveElement(document.activeElement);
      }

      if (this.productForm && this.productForm.onSubmitHandler) {
        const submitEvent = new Event("submit", { cancelable: true, bubbles: true });
        this.surveyForm.dispatchEvent(submitEvent);
      } else {
        this.surveyForm.submit();
      }
    }

    /* =========================
       Helpers
       ========================= */
    formatMoney(cents) {
      const format = window.theme?.moneyFormat || "€{{amount_with_comma_separator}}";
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents, format);
      }
      return (cents / 100).toFixed(2).replace(".", ",");
    }

    resetSurvey() {
      this.answers = { gender: null, locations: [], partner: null, partner_gender: null, quantity: null, photos: [] };
      this.selectedItems = [];
      this.currentIndex = 0;
      if (this.pdfInput) {
        this.pdfInput.value = "";
        this.pdfInput.removeAttribute("name");
      }
      if (this.photosInput) {
        this.photosInput.value = "";
      }
      this.__purchaseAttempted = false;
      this.handleError("");
      this.showResultsView(false);
      if (this.informationContainer) this.informationContainer.style.display = "flex";
      this.renderCurrentStep();
      this.updateBuyButton();
    }

    hideDefaultProductForm() {
      const root = this.closest('section[id^="MainProduct-"]') || this.closest(".product") || this;
      const variantPicker = root.querySelector(".variant-picker");
      const buyButtons = root.querySelector(".product-form__buttons");
      if (variantPicker) variantPicker.style.display = "none";
      if (buyButtons) buyButtons.style.display = "none";
    }

    showFallback() {
      this.style.display = "none";
      const root = this.closest('section[id^="MainProduct-"]') || this.closest(".product") || document;
      const variantPicker = root.querySelector(".variant-picker");
      const buyButtons = root.querySelector(".product-form__buttons");
      if (variantPicker) variantPicker.style.display = "block";
      if (buyButtons) buyButtons.style.display = "block";
    }

    patchCartNotification() {
      setTimeout(() => {
        const cartNotification = document.querySelector("cart-notification");
        if (cartNotification) {
          cartNotification.getSectionInnerHTML = function (html, selector = ".shopify-section") {
            try {
              const parsed = new DOMParser().parseFromString(html, "text/html");
              const element = parsed.querySelector(selector);
              return element ? element.innerHTML : "";
            } catch (error) {
              return "";
            }
          };
        }
      }, 100);
    }
  }

  if (!customElements.get("treatment-survey")) {
    customElements.define("treatment-survey", TreatmentSurvey);
  }
  window.TreatmentSurvey = TreatmentSurvey;
})();
